import React from "react";
import Reveal from "./Reveal";

const PROJECTS = [
  {
    title: "Junibo",
    emoji: "🏪",
    video: "/junibo.mp4",
    description:
      "A micro digital e-commerce site with social authentication, Stripe payments, and an admin sales-analytics dashboard.",
    tags: ["Node.js", "Stripe", "Tailwind", "OAuth"],
    link: {
      href: "https://github.com/AlexanderLasson/juniboMarket",
      label: "View on GitHub",
      icon: "fab fa-github",
    },
  },
  {
    title: "LegalLite",
    emoji: "📜",
    video: "/LegalLite.mp4",
    description:
      "A Terms-of-Service scanner that checks agreements for validity, using spaCy to automatically annotate and surface specific legal clauses.",
    tags: ["Python", "spaCy", "NLP"],
    link: {
      href: "https://github.com/AlexanderLasson/LegalLite",
      label: "View on GitHub",
      icon: "fab fa-github",
    },
  },
  {
    title: "LinkJunction",
    emoji: "📖",
    video: "/HLS.mp4",
    description:
      "A social bookmarking platform with a REST API, email auth, token-based collaboration, and Chrome bookmark importing. Freelance build for a client.",
    tags: ["REST API", "AJAX", "Auth", "Freelance"],
    link: {
      href: "https://hyperlinksaver.com",
      label: "hyperlinksaver.com",
      icon: "fas fa-arrow-up-right-from-square",
    },
  },
  {
    title: "TypeLeap",
    emoji: "🐸",
    video: "/typeleap.mp4",
    description:
      "A frog-themed Monkeytype clone with a Firebase-backed global leaderboard.",
    tags: ["JavaScript", "Firebase"],
    link: {
      href: "https://typeleap.online/",
      label: "typeleap.online",
      icon: "fas fa-arrow-up-right-from-square",
    },
  },
  {
    title: "Plastic Blame",
    emoji: "🫵",
    image: "/PlasticBlame.png",
    description:
      "A VS Code extension that shows the last author of the current line via Plastic SCM's cm annotate — git blame, but for Plastic SCM.",
    tags: ["TypeScript", "VS Code API", "Plastic SCM"],
    link: {
      href: "https://marketplace.visualstudio.com/items?itemName=DeadFault.plastic-blame",
      label: "VS Code Marketplace",
      icon: "fas fa-arrow-up-right-from-square",
    },
  },
];

const ProjectCard = ({ project }) => (
  <article
    className="group bg-dusk-900/70 border border-white/10 rounded-2xl overflow-hidden
               hover:border-ember/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-ember/10
               transition-all duration-300 flex flex-col"
  >
    <div className="aspect-video overflow-hidden bg-dusk-900">
      {project.video ? (
        <video
          src={project.video}
          autoPlay
          muted
          loop
          playsInline
          aria-label={`${project.title} preview`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <img
          src={project.image}
          alt={`${project.title} preview`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      )}
    </div>
    <div className="p-6 flex flex-col flex-1">
      <h3 className="font-display text-xl font-bold text-white mb-2">
        <span className="mr-2">{project.emoji}</span>
        {project.title}
      </h3>
      <p className="text-white/70 text-sm leading-relaxed flex-1">
        {project.description}
      </p>
      <div className="flex flex-wrap gap-2 mt-4 mb-5">
        {project.tags.map((tag) => (
          <span
            key={tag}
            className="text-xs font-medium text-blush/90 bg-white/5 border border-white/10 rounded-full px-2.5 py-1"
          >
            {tag}
          </span>
        ))}
      </div>
      <a
        href={project.link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm font-semibold text-ember hover:text-white transition-colors"
      >
        <i className={project.link.icon}></i>
        {project.link.label}
      </a>
    </div>
  </article>
);

const Projects = () => (
  <section id="projects" className="relative min-h-screen py-28 px-6">
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <p className="text-sm font-semibold tracking-[0.3em] uppercase text-ember text-shadow mb-2">
          02 · Projects
        </p>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white text-shadow-strong mb-4">
          Things I've shipped
        </h2>
        <p className="text-white/85 text-lg max-w-2xl text-shadow mb-12">
          Full products, not toy demos — payments, auth, NLP pipelines, editor
          tooling, and live deployments for real users.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROJECTS.map((project, i) => (
          <Reveal key={project.title} delay={(i % 3) * 100}>
            <ProjectCard project={project} />
          </Reveal>
        ))}

        {/* GitHub CTA card */}
        <Reveal delay={200}>
          <a
            href="https://github.com/AlexanderLasson"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center justify-center text-center gap-4 h-full min-h-[16rem]
                       bg-dusk-900/60 border border-dashed border-white/20 rounded-2xl p-8
                       hover:border-ember/60 hover:bg-black/40 transition-all duration-300"
          >
            <i className="fab fa-github text-5xl text-white/60 group-hover:text-ember transition-colors"></i>
            <div>
              <p className="font-display font-semibold text-white text-lg">
                More on GitHub
              </p>
              <p className="text-white/60 text-sm mt-1">
                Experiments, contributions, and whatever I'm building next.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-ember">
              github.com/AlexanderLasson
              <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
            </span>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Projects;
